import { useState, useEffect } from 'react';
import { Lock, Unlock, Shield, AlertTriangle, Search, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { validateAccessCode, getPricing } from '@/lib/api';

const PricingPage = () => {
  const [accessCode, setAccessCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [pricingData, setPricingData] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [proxyEnabled, setProxyEnabled] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  useEffect(() => {
    // Add noindex meta tag
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();

    if (!accessCode.trim()) {
      toast.error('Please enter an access code');
      return;
    }

    setIsValidating(true);

    try {
      // First validate the code
      const validationRes = await validateAccessCode(accessCode);
      setUserEmail(validationRes.data.email);

      // Then fetch pricing
      const pricingRes = await getPricing(accessCode);
      setPricingData(pricingRes.data.pricing);
      setIsUnlocked(true);
      setShowInfoDialog(true); // Show info dialog on first unlock
      toast.success('Access granted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired access code');
    } finally {
      setIsValidating(false);
    }
  };

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <img 
              src="/softmaya-logo.png" 
              alt="Softmaya" 
              className="h-12 mx-auto mb-6"
            />
            <h1 className="font-serif text-3xl font-semibold text-foreground mb-2">
              Pricing Access
            </h1>
            <p className="text-muted-foreground">
              Enter your access code to view confidential pricing information.
            </p>
          </div>

          {/* Code Input */}
          <Card className="glass-card">
            <CardContent className="pt-6">
              <form onSubmit={handleUnlock} className="space-y-6">
                <div className="space-y-2">
                  <Input
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    placeholder="SM-XXXXXX-XXXX"
                    className="text-center font-mono text-lg tracking-wider bg-background/50 h-14"
                    data-testid="access-code-input"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  disabled={isValidating}
                  className="w-full gold-glow-hover"
                  data-testid="unlock-btn"
                >
                  {isValidating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Validating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Unlock className="w-4 h-4" />
                      Unlock Pricing
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground">
            Don't have an access code?{' '}
            <a href="/access" className="text-primary hover:underline">
              Request NDA Access
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Filter providers based on search
  const filteredData = pricingData.filter(item =>
    item.provider.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleProxyToggle = (checked) => {
    setProxyEnabled(checked);
    toast.success(checked ? 'Proxy pricing enabled' : 'Direct pricing enabled');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Confidential Banner */}
      <div className="bg-destructive/20 border-b border-destructive/30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-center gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive" />
          <span className="text-sm font-medium text-destructive">
            CONFIDENTIAL - NDA Protected Information
          </span>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-border bg-card/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="/softmaya-logo.png" 
              alt="Softmaya" 
              className="h-10 w-auto"
            />
            <div>
              <p className="text-sm text-muted-foreground">Provider Pricing</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Authenticated as</p>
            <p className="text-sm font-medium text-foreground">{userEmail}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="font-serif text-3xl font-semibold text-foreground mb-2">
              Provider Pricing Schedule
            </h2>
            <p className="text-muted-foreground">
              The following pricing is confidential and subject to the terms of your NDA.
            </p>
          </div>
          
          {/* Proxy Toggle */}
          <div className="flex items-center gap-4 p-4 rounded-lg bg-card border border-border">
            <div className="flex items-center gap-2">
              <Switch
                id="proxy-mode"
                checked={proxyEnabled}
                onCheckedChange={handleProxyToggle}
                data-testid="proxy-toggle"
              />
              <Label htmlFor="proxy-mode" className="font-medium cursor-pointer">
                {proxyEnabled ? 'Proxy Pricing' : 'Direct Pricing'}
              </Label>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setShowInfoDialog(true)}
              data-testid="info-btn"
            >
              <Info className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search provider..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background/50"
              data-testid="search-provider-input"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {searchQuery ? (
              <>Found {filteredData.length} of {pricingData.length} providers</>
            ) : (
              <>Total: {pricingData.length} providers</>
            )}
          </p>
        </div>

        <Card className="glass-card vault-unlock">
          <CardContent className="p-0">
            <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 350px)', minHeight: '500px' }}>
              <Table className="table-zebra" data-testid="pricing-table">
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-semibold text-foreground">Provider</TableHead>
                    <TableHead className="font-semibold text-foreground text-right">
                      {proxyEnabled ? 'Proxy Fee (%)' : 'Fee (%)'}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item, index) => {
                    const showProxy = proxyEnabled && item.has_proxy;
                    const fee = showProxy ? item.proxy_fee : item.fee;
                    const isProxyNA = proxyEnabled && !item.has_proxy;
                    
                    return (
                      <TableRow key={index} className="border-border">
                        <TableCell className="font-medium">
                          {item.provider}
                          {item.has_proxy && (
                            <span className="ml-2 text-xs text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">
                              Proxy
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={`font-mono text-right ${isProxyNA ? 'text-muted-foreground' : 'text-primary'}`}>
                          {isProxyNA ? 'N/A' : `${fee.toFixed(1)}%`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 p-6 rounded-lg bg-muted/30 border border-border">
          <h3 className="font-medium text-foreground mb-2">Important Notice</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            This pricing information is provided under the terms of your Non-Disclosure Agreement with Softmaya. 
            Unauthorized disclosure or distribution of this information is strictly prohibited and may result in 
            legal action. If you have any questions about this pricing, please contact your account representative.
          </p>
        </div>

        {/* Info Dialog */}
        <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-primary" />
                Pricing Mode Information
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="p-4 rounded-lg bg-muted/50 border border-border">
                <h4 className="font-semibold text-foreground mb-2">Direct Pricing</h4>
                <p className="text-sm text-muted-foreground">
                  Standard fees for direct integration with providers. Use this when you have a direct contract with the provider.
                </p>
              </div>
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <h4 className="font-semibold text-foreground mb-2">Proxy Pricing</h4>
                <p className="text-sm text-muted-foreground">
                  Fees when using Softmaya as a proxy/aggregator. This includes additional service fees for aggregation, support, and unified API access.
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Toggle between modes using the switch at the top of the pricing table to compare rates.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowInfoDialog(false)} data-testid="close-info-btn">
                Got it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default PricingPage;
